import * as THREE from 'three'
import { heightAt, noise3, rawIslandEnvelope, sampleTerrainRadius } from '../terrain'
import type { VillagePlacement } from './types'
import { generateLayout } from './layout'

function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

interface Candidate {
  nx: number
  ny: number
  nz: number
  score: number
}

function makePlacement(
  c: { nx: number; ny: number; nz: number },
): { normal: THREE.Vector3; position: THREE.Vector3; tangentX: THREE.Vector3; tangentZ: THREE.Vector3 } {
  const normal = new THREE.Vector3(c.nx, c.ny, c.nz).normalize()
  const terrainR = sampleTerrainRadius(c.nx, c.ny, c.nz)
  const position = normal.clone().multiplyScalar(terrainR)
  const up = normal.clone()
  const arbitrary = Math.abs(up.y) < 0.9
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0)
  const tangentX = new THREE.Vector3().crossVectors(up, arbitrary).normalize()
  const tangentZ = new THREE.Vector3().crossVectors(tangentX, up).normalize()
  return { normal, position, tangentX, tangentZ }
}

/**
 * Fast scan for village sites using cheap checks only.
 */
export function findVillageSites(count: number, seed: number = 12345): VillagePlacement[] {
  const rng = mulberry32(seed)
  const candidates: Candidate[] = []

  const totalSamples = 1500
  for (let i = 0; i < totalSamples; i++) {
    const phi = Math.acos(1 - 2 * (i + 0.5) / totalSamples)
    const theta = Math.PI * (1 + Math.sqrt(5)) * i

    const nx = Math.sin(phi) * Math.cos(theta)
    const ny = Math.cos(phi)
    const nz = Math.sin(phi) * Math.sin(theta)

    const rawEnv = rawIslandEnvelope(nx, ny, nz)
    if (rawEnv < 0.30 || rawEnv > 0.55) continue

    // Must be clearly above water — reject underwater/borderline positions
    const h = heightAt(nx, ny, nz)
    if (h < 0.005) continue

    // Reject steep terrain — sample nearby heights and check slope
    const SLOPE_EPS = 0.015
    const slopeUp = new THREE.Vector3(nx, ny, nz)
    const slopeArb = Math.abs(ny) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const slopeTx = new THREE.Vector3().crossVectors(slopeUp, slopeArb).normalize()
    const slopeTz = new THREE.Vector3().crossVectors(slopeTx, slopeUp).normalize()
    const sp1 = new THREE.Vector3().copy(slopeUp).addScaledVector(slopeTx, SLOPE_EPS).normalize()
    const sp2 = new THREE.Vector3().copy(slopeUp).addScaledVector(slopeTx, -SLOPE_EPS).normalize()
    const sp3 = new THREE.Vector3().copy(slopeUp).addScaledVector(slopeTz, SLOPE_EPS).normalize()
    const sp4 = new THREE.Vector3().copy(slopeUp).addScaledVector(slopeTz, -SLOPE_EPS).normalize()
    const slopeX = Math.abs(heightAt(sp1.x, sp1.y, sp1.z) - heightAt(sp2.x, sp2.y, sp2.z)) / (2 * SLOPE_EPS)
    const slopeZ = Math.abs(heightAt(sp3.x, sp3.y, sp3.z) - heightAt(sp4.x, sp4.y, sp4.z)) / (2 * SLOPE_EPS)
    const slopeMag = Math.sqrt(slopeX * slopeX + slopeZ * slopeZ)
    if (slopeMag > 0.12) continue // reject slopes steeper than ~7 degrees

    const dw = 0.10
    const wx = nx + dw * noise3(nx * 3.5 + 7.3, ny * 3.5 + 2.1, nz * 3.5 + 5.9)
    const wy = ny + dw * noise3(nx * 3.5 + 47.1, ny * 3.5 + 31.7, nz * 3.5 + 19.3)
    const wz = nz + dw * noise3(nx * 3.5 + 91.7, ny * 3.5 + 67.3, nz * 3.5 + 43.1)
    const wl = Math.sqrt(wx * wx + wy * wy + wz * wz)
    const px = wx / wl, py = wy / wl, pz = wz / wl
    const rawMtn = noise3(px * 2.0, py * 2.0, pz * 2.0) * 0.5 + 0.5
    if (rawMtn > 0.42) continue

    const envScore = 1 - Math.abs(rawEnv - 0.40) * 3
    const mtnScore = 1 - rawMtn / 0.42
    const score = envScore * 0.5 + mtnScore * 0.5

    candidates.push({ nx, ny, nz, score })
  }

  candidates.sort((a, b) => b.score - a.score)

  const MIN_SEPARATION = 0.15
  const selected: Candidate[] = []

  for (const c of candidates) {
    if (selected.length >= count) break
    let tooClose = false
    for (const s of selected) {
      const dot = c.nx * s.nx + c.ny * s.ny + c.nz * s.nz
      const angDist = Math.acos(Math.min(1, Math.max(-1, dot)))
      if (angDist < MIN_SEPARATION) { tooClose = true; break }
    }
    if (!tooClose) selected.push(c)
  }

  console.log(`[Villages] Found ${candidates.length} candidates, selected ${selected.length} sites`)

  return selected.map((c, i) => {
    const { normal, position, tangentX, tangentZ } = makePlacement(c)
    const sizes: Array<'small' | 'medium' | 'large'> = ['small', 'medium', 'large']
    const size = sizes[Math.floor(rng() * 3)]
    const worldInfo = { nx: c.nx, ny: c.ny, nz: c.nz, tangentX, tangentZ }
    const layout = generateLayout(size, seed + i * 777, worldInfo)
    return { normal, position, tangentX, tangentZ, layout, seed: seed + i * 777, size }
  })
}

/**
 * Scan for dock sites on sand-colored terrain (the beach strip just above water).
 * Simple approach: find points in the sand height band, compute which way is ocean.
 */
export function findDockSites(count: number, seed: number = 54321): VillagePlacement[] {
  interface DockCandidate {
    nx: number; ny: number; nz: number
    outDirX: number; outDirY: number; outDirZ: number
    score: number
  }
  const candidates: DockCandidate[] = []

  // Sand zone: heightAt roughly 0.0003–0.005 (the beach strip the terrain shader colors sand)
  const SAND_MIN = 0.0003
  const SAND_MAX = 0.005
  const GRAD_EPS = 0.008
  const totalSamples = 5000

  for (let i = 0; i < totalSamples; i++) {
    const phi = Math.acos(1 - 2 * (i + 0.5) / totalSamples)
    const theta = Math.PI * (1 + Math.sqrt(5)) * i

    const nx = Math.sin(phi) * Math.cos(theta)
    const ny = Math.cos(phi)
    const nz = Math.sin(phi) * Math.sin(theta)

    // Must be on an island
    const rawEnv = rawIslandEnvelope(nx, ny, nz)
    if (rawEnv < 0.02 || rawEnv > 0.50) continue

    // Must be in the sand/beach height band
    const h = heightAt(nx, ny, nz)
    if (h < SAND_MIN || h > SAND_MAX) continue

    // Compute gradient to find which way is downhill (toward ocean)
    const up = new THREE.Vector3(nx, ny, nz)
    const arb = Math.abs(ny) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const tx = new THREE.Vector3().crossVectors(up, arb).normalize()
    const tz = new THREE.Vector3().crossVectors(tx, up).normalize()

    const px = new THREE.Vector3().copy(up).addScaledVector(tx, GRAD_EPS).normalize()
    const mx = new THREE.Vector3().copy(up).addScaledVector(tx, -GRAD_EPS).normalize()
    const pz = new THREE.Vector3().copy(up).addScaledVector(tz, GRAD_EPS).normalize()
    const mz = new THREE.Vector3().copy(up).addScaledVector(tz, -GRAD_EPS).normalize()

    const dhx = heightAt(px.x, px.y, px.z) - heightAt(mx.x, mx.y, mx.z)
    const dhz = heightAt(pz.x, pz.y, pz.z) - heightAt(mz.x, mz.y, mz.z)
    const gradLen = Math.sqrt(dhx * dhx + dhz * dhz)
    if (gradLen < 0.00005) continue

    // Outward = downhill = negative gradient
    const outDir = new THREE.Vector3()
      .addScaledVector(tx, -dhx / gradLen)
      .addScaledVector(tz, -dhz / gradLen)
      .normalize()

    // Quick check: ~100 units out should be lower / underwater
    const checkPt = new THREE.Vector3(nx, ny, nz).addScaledVector(outDir, 0.01).normalize()
    if (heightAt(checkPt.x, checkPt.y, checkPt.z) > h) continue

    // Score: prefer lower (closer to water)
    const score = 1 - h / SAND_MAX

    candidates.push({ nx, ny, nz, outDirX: outDir.x, outDirY: outDir.y, outDirZ: outDir.z, score })
  }

  candidates.sort((a, b) => b.score - a.score)

  // Greedy pick with minimum separation
  const MIN_SEP = 0.06
  const selected: DockCandidate[] = []
  for (const c of candidates) {
    if (selected.length >= count) break
    let tooClose = false
    for (const s of selected) {
      const dot = c.nx * s.nx + c.ny * s.ny + c.nz * s.nz
      if (Math.acos(Math.min(1, Math.max(-1, dot))) < MIN_SEP) { tooClose = true; break }
    }
    if (!tooClose) selected.push(c)
  }

  console.log(`[Docks] Found ${candidates.length} beach candidates, selected ${selected.length} sites`)

  return selected.map((c, i) => {
    const { normal, position, tangentX, tangentZ } = makePlacement(c)

    const outDir = new THREE.Vector3(c.outDirX, c.outDirY, c.outDirZ)
    const projX = outDir.dot(tangentX)
    const projZ = outDir.dot(tangentZ)
    const rotation = Math.atan2(-projX, projZ)

    const layout = {
      structures: [{ type: 'dock' as const, localX: 0, localZ: 0, rotation }],
      paths: [],
      fencePosts: [],
      fenceRadius: 0,
      props: [],
    }

    return {
      normal, position, tangentX, tangentZ,
      layout,
      seed: seed + i * 333,
      size: 'small' as const,
    }
  })
}
